//@ts-nocheck
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Formik } from "formik";
import { observer } from "mobx-react-lite";
import React from "react";
import { object, string } from "yup";
import { FFormContext, FFormField } from "../primitives/forms/FormField";
import { IPaymentInfoStore, ISupportedCountryStore } from "../stores";
import { scrollToId, scrollToTop } from "../utils/functions";
import paymentInfoData from "./formData/paymentInfo.json";

export const PaymentInfoForm = observer(
  (props: {
    onSubmit: any;
    innerRef?: any;
    supportedCountry?: ISupportedCountryStore;
    banks: Array<string>;
    loading?: boolean;
    initialValues: {
      bankName: string;
      accountName: string;
      accountNumber: string;
    };
    isReadOnly?: boolean;
  }) => {
    const {
      banks,
      supportedCountry,
      initialValues,
      onSubmit,
      loading,
      innerRef,
      isReadOnly,
    } = props;

    React.useEffect(() => scrollToTop(), []);
    const [showFormError, setShowFormError] = React.useState(false);

    const onSubmitClick = (errors = {}) => {
      if (errors) {
        if (Object.values(errors).length) {
          setShowFormError(true);
          scrollToId("form-header-section");
        } else {
          setShowFormError(false);
        }
      }
    };
    let validationSchema = object().shape({
      bankName: string()
        .trim()
        .required(paymentInfoData.validationData.bankName.required),
      accountNumber: string()
        .trim()
        .required(paymentInfoData.validationData.accountNumber.required)
        .matches(/^\d+$/, {
          excludeEmptyString: true,
          message: paymentInfoData.validationData.accountNumber.digit,
        })
        .length(10, paymentInfoData.validationData.accountNumber.length),
      sortCode: string(),
      taxId: string(),
      accountName: string().required(
        paymentInfoData.validationData.accountName.required,
      ),
    });

    return (
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        ref={innerRef}
        onSubmit={(values) => {
          return onSubmit(values);
        }}
        render={({ handleSubmit, errors }) => {
          return (
            <form
              onSubmit={(values) => {
                handleSubmit(values);
                onSubmitClick(errors);
              }}
            >
              <Stack
                spacing={6}
                className="payment-info-form"
                id="form-header-section"
              >
                <Stack mb={4}>
                  <Text fontWeight="bold" fontSize={["3xl", "4xl"]}>
                    {paymentInfoData.formTitle.header}
                  </Text>
                  <Text color="gray.700">
                    {paymentInfoData.formTitle.subHeader}
                  </Text>
                  {showFormError ? (
                    <Box pt={2}>
                      <Alert rounded="lg" status="error">
                        <AlertIcon />
                        {paymentInfoData.formTitle.errorMessage}
                      </Alert>
                    </Box>
                  ) : (
                    <Box pt={2}>
                      <Alert rounded="lg" status="info">
                        <AlertIcon />
                        {paymentInfoData.formTitle.alertMessage}
                      </Alert>
                    </Box>
                  )}
                </Stack>
                <FFormField
                  name="bankName"
                  type="select"
                  key={`${banks.length > 0}`}
                  options={banks.map((o) => ({ label: o, value: o }))}
                  disabled={isReadOnly}
                  title={isReadOnly && "This is disabled"}
                />

                <Flex
                  direction={["column", "row"]}
                  justifyContent="space-between"
                >
                  <FFormField
                    name="accountNumber"
                    disabled={isReadOnly}
                    title={isReadOnly && "This is disabled"}
                  />

                  <Box width={6} height={6} />

                  <FFormField name="sortCode" />
                </Flex>

                <Flex
                  direction={["column", "row"]}
                  justifyContent="space-between"
                >
                  <FFormField
                    name="accountName"
                    disabled={isReadOnly}
                    title={isReadOnly && "This is disabled"}
                  />

                  <Box width={6} height={6} />
                  <FFormField name="taxId" />
                </Flex>

                <Box pt={[4, 5]}>
                  <Button
                    type="submit"
                    size="lg"
                    colorScheme="blue"
                    isLoading={loading}
                    loadingText={paymentInfoData.loadingText}
                  >
                    {paymentInfoData.saveAndContinue}
                  </Button>
                </Box>
              </Stack>
            </form>
          );
        }}
      />
    );
  },
);

export type PaymentProps = {
  store: IPaymentInfoStore;
  loading?: boolean;
  isReadOnly?: boolean;
  supportedCountry?: ISupportedCountryStore;
  [key: string]: any;
  onSubmit?: (params?: any) => any;
};

const PaymentInfo: React.FC<PaymentProps> = observer(
  ({
    store,
    onSubmit,
    loading,
    supportedCountry,
    viewModel,
    isReadOnly,
    ...rest
  }) => {
    return (
      <FFormContext.Provider
        value={{ placeholders: paymentInfoData.placeholders } as any}
      >
        <PaymentInfoForm
          key={store.bankName}
          supportedCountry={supportedCountry}
          initialValues={store.formValues}
          banks={store.banks}
          loading={loading}
          onSubmit={onSubmit}
          isReadOnly={isReadOnly}
          {...rest}
        />
      </FFormContext.Provider>
    );
  },
);

export default PaymentInfo;
